import { useUnit } from "effector-react";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@core/constants/app";
import { chatService } from "../service/chatService";
import {
  $chatStore,
  $currentConversationMessages,
  $currentConversationHasMore,
  $currentConversationBlockStatus,
  $chatLoading,
  $chatLoadingMore,
  $sendingMessage,
  $blockingUser,
  fetchConversationHistoryRequested,
  loadMoreMessagesRequested,
  sendMessageRequested,
  markConversationAsReadRequested,
  deleteConversationRequested,
  deleteConversationFx,
  blockUserRequested,
  setCurrentConversation,
} from "../store/chatStore";
import { $authStore } from "../../auth/store/auth";
import { BlockUserPayload } from "../types";

/**
 * ViewModel hook for the open conversation / chat page.
 * @param conversationId - The conversation ID from the URL param
 */
export const useChatViewModel = (conversationId?: string) => {
  const navigate = useNavigate();
  const chatStore = useUnit($chatStore);
  const messages = useUnit($currentConversationMessages);
  const hasMoreMessages = useUnit($currentConversationHasMore);
  const loading = useUnit($chatLoading);
  const loadingMore = useUnit($chatLoadingMore);
  const sendingMessage = useUnit($sendingMessage);
  const deletingConversation = useUnit(deleteConversationFx.pending);
  const blockStatus = useUnit($currentConversationBlockStatus);
  const blockingUser = useUnit($blockingUser);
  const authStore = useUnit($authStore);

  const currentUser = authStore.user;
  const error = chatStore.error;

  useEffect(() => {
    if (!conversationId) return;

    setCurrentConversation(conversationId);
    fetchConversationHistoryRequested(conversationId);

    // Only mark as read if there are unread messages globally
    if (chatStore.unreadConversationsCount > 0) {
      markConversationAsReadRequested(conversationId);
    }

    return () => {
      setCurrentConversation(null);
    };
  }, [conversationId]); // We intentionally do not include chatStore in dependencies to avoid re-triggering on every store update

  const sendMessage = (messageText: string, fid?: number) => {
    if (!conversationId || (!messageText.trim() && !fid)) return;
    sendMessageRequested({ conversationId, message: messageText, fid });
  };

  const refreshChat = () => {
    if (conversationId) {
      fetchConversationHistoryRequested(conversationId);
    }
  };

  const loadMoreMessages = () => {
    if (conversationId && hasMoreMessages && !loadingMore) {
      loadMoreMessagesRequested(conversationId);
    }
  };

  const deleteConversation = useCallback(() => {
    if (conversationId) {
      deleteConversationRequested(conversationId);
    }
  }, [conversationId]);

  const blockUser = useCallback(
    (payload: Omit<BlockUserPayload, "blocked_uid">) => {
      const otherUid = chatStore.chatUsers[conversationId ?? ""]?.id;
      if (!conversationId || !otherUid) return;
      blockUserRequested({ ...payload, blocked_uid: otherUid });
    },
    [conversationId, chatStore.chatUsers],
  );

  useEffect(() => {
    const unsubscribe = deleteConversationFx.doneData.watch((deletedId) => {
      if (deletedId === conversationId) {
        navigate(ROUTES.CHAT_INBOX);
      }
    });
    return unsubscribe;
  }, [conversationId, navigate]);

  return {
    messages,
    loading,
    loadingMore,
    hasMoreMessages,
    sendingMessage,
    deletingConversation,
    blockStatus,
    blockingUser,
    blockUser,
    error,
    currentUser,
    sendMessage,
    refreshChat,
    loadMoreMessages,
    deleteConversation,
  };
};

/**
 * ViewModel hook for the conversations inbox sidebar.
 */
export const useChatInboxViewModel = () => {
  const {
    conversations,
    loadingConversations,
    conversationsError,
    hasMoreConversations,
    unreadConversationsCount,
  } = useUnit($chatStore);
  return {
    conversations,
    loading: loadingConversations,
    error: conversationsError,
    hasMore: hasMoreConversations,
    unreadCount: unreadConversationsCount,
  };
};

/**
 * Hook for starting a private conversation from a user card / user detail page.
 * Calls /api/v1/private_conversation/{uid}, then navigates to /messages/{conversation_id}.
 */
export const useStartConversation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const startConversation = useCallback(
    async (uid: number | string) => {
      setLoading(true);
      try {
        const conversationId = await chatService.getOrCreatePrivateConversation(
          uid.toString(),
        );
        navigate(`/messages/${conversationId}`);
      } catch (err) {
        console.error("Failed to open conversation:", err);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return { startConversation, loading };
};
